import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'dark' | 'light';

export const APPROVED_ACCENTS = {
  teal: { label: 'Teal', value: 'oklch(0.74 0.11 195)' },
  cyan: { label: 'Cyan', value: 'oklch(0.80 0.11 225)' },
  green: { label: 'Green', value: 'oklch(0.78 0.13 150)' },
  rose: { label: 'Rose', value: 'oklch(0.70 0.16 15)' },
} as const;

export type AccentId = keyof typeof APPROVED_ACCENTS;
export type AccentChoice = AccentId | 'custom';
export const DEFAULT_ACCENT: AccentId = 'teal';
const DEFAULT_CUSTOM_ACCENT = '#58d4bd';
const STORAGE_KEY = 'songcomposer.theme';

type StoredTheme = { mode?: unknown; accent?: unknown; customAccent?: unknown };
type ThemeContextValue = {
  mode: ThemeMode;
  accent: AccentChoice;
  customAccent: string;
  setMode: (mode: ThemeMode) => void;
  setAccent: (accent: AccentId) => void;
  setCustomAccent: (accent: string) => void;
  toggleMode: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'dark' || value === 'light';
}

function isAccentId(value: unknown): value is AccentId {
  return typeof value === 'string' && Object.hasOwn(APPROVED_ACCENTS, value);
}

function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);
}

function readStoredTheme(): { mode: ThemeMode; accent: AccentChoice; customAccent: string } {
  const fallback = {
    mode: 'dark' as const,
    accent: DEFAULT_ACCENT as AccentChoice,
    customAccent: DEFAULT_CUSTOM_ACCENT,
  };
  if (typeof window === 'undefined') return fallback;

  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') as StoredTheme;
    return {
      mode: isThemeMode(stored.mode) ? stored.mode : fallback.mode,
      accent:
        stored.accent === 'custom' || isAccentId(stored.accent) ? stored.accent : fallback.accent,
      customAccent: isHexColor(stored.customAccent) ? stored.customAccent : fallback.customAccent,
    };
  } catch {
    return fallback;
  }
}

function relativeLuminance(hex: string): number {
  const channels = [1, 3, 5].map(
    (offset) => Number.parseInt(hex.slice(offset, offset + 2), 16) / 255,
  );
  const [red, green, blue] = channels.map((channel) =>
    channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4,
  );
  return 0.2126 * red! + 0.7152 * green! + 0.0722 * blue!;
}

function contrastRatio(first: string, second: string): number {
  const [lighter, darker] = [relativeLuminance(first), relativeLuminance(second)].sort(
    (a, b) => b - a,
  );
  return (lighter! + 0.05) / (darker! + 0.05);
}

function inkForAccent(accent: string): string {
  const darkInk = '#102425';
  const lightInk = '#ffffff';
  return contrastRatio(accent, darkInk) >= contrastRatio(accent, lightInk) ? darkInk : lightInk;
}

export function ThemeProvider({ children }: { children?: ReactNode }) {
  const initial = useMemo(readStoredTheme, []);
  const [mode, setMode] = useState<ThemeMode>(initial.mode);
  const [accent, setAccentChoice] = useState<AccentChoice>(initial.accent);
  const [customAccent, setCustomAccentValue] = useState(initial.customAccent);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = mode;
    root.dataset.accent = accent;
    if (accent === 'custom') {
      root.style.setProperty('--accent', customAccent);
      root.style.setProperty('--accent-ink', inkForAccent(customAccent));
    } else {
      root.style.removeProperty('--accent');
      root.style.removeProperty('--accent-ink');
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode, accent, customAccent }));
    } catch {
      // Storage may be unavailable in hardened browser contexts; the in-memory theme still works.
    }
  }, [accent, customAccent, mode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      accent,
      customAccent,
      setMode,
      setAccent: setAccentChoice,
      setCustomAccent: (next) => {
        if (!isHexColor(next)) return;
        setCustomAccentValue(next);
        setAccentChoice('custom');
      },
      toggleMode: () => setMode((current) => (current === 'dark' ? 'light' : 'dark')),
    }),
    [accent, customAccent, mode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside ThemeProvider');
  return value;
}
